import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import LaneNav from "../components/LaneNav";
import { toast } from "sonner";
import { Vault as VaultIcon, Plus, ArrowRight, Camera, Upload, Trash, Receipt, CalendarBlank, ShieldCheck, Warning, EnvelopeSimple, Clock, DownloadSimple } from "@phosphor-icons/react";
import { SkeletonGrid } from "../components/Skeleton";
import { formatDateNZ } from "../lib/format";
import FeedbackCTA from "../components/FeedbackCTA";
import api, { API } from "../lib/api";

function UnifiedStatusPill({ it }) {
  const d = it.warranty_days_left;
  const ending = d != null && d >= 0 && d <= 30;
  const stillUnderWarranty = it.status === "warranty" || (it.source === "purchase" && it.status === "protected" && d != null && d >= 0);
  const showProtected = it.source === "purchase" && it.status === "protected";

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      {stillUnderWarranty ? (
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-mono-pg uppercase tracking-widest font-bold px-1.5 py-0.5 ${ending ? "bg-[#D97706] text-white" : "bg-[#16A34A] text-white"}`}
          data-testid="status-pill-warranty"
        >
          <ShieldCheck size={11} weight="fill" /> Under warranty{d != null ? ` · ${d}d left` : ""}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[10px] font-mono-pg uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#e4e4e7] text-[#52525b]" data-testid="status-pill-expired">
          <Clock size={11} weight="fill" /> Warranty ended
        </span>
      )}
      {showProtected && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-mono-pg uppercase tracking-widest font-bold px-1.5 py-0.5 border border-[#002FA7] text-[#002FA7]"
          data-testid="status-pill-protected"
        >
          <ShieldCheck size={10} weight="fill" /> Price-protected{it.tracking_days_left != null ? ` · ${it.tracking_days_left}d` : ""}
        </span>
      )}
    </span>
  );
}

export default function Vault() {
  const [items, setItems] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/vault"),
        api.get("/vault/_meta/plan"),
      ]);
      setItems(Array.isArray(a.data) ? a.data : []);
      setPlan(b.data);
    } catch (e) {
      toast.error("Could not load your vault");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const del = async (vault_id) => {
    if (!confirm("Delete this receipt and warranty record? This can't be undone.")) return;
    try {
      await api.delete(`/vault/${vault_id}`);
      toast.success("Removed from vault");
      load();
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f7f8]">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="vault-page">
        <LaneNav active="vault" />
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="pg-overline">Vault</div>
            <h1 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight mt-2">
              Every purchase, organised<br/>and protected.
            </h1>
            <p className="text-[#52525b] mt-3 max-w-xl text-sm leading-relaxed">
              No more digging through emails, drawers, or old folders when you need proof of purchase. Receipts, warranties, and the items you're still price-protecting all live here, ready when you need them.
            </p>
          </div>
          <Link to="/dashboard/vault/new" className="bg-[#002FA7] text-white font-bold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#00207A]" data-testid="vault-add-cta">
            <Plus size={18} weight="bold" /> Add a receipt
          </Link>
        </div>

        {/* ASSET-REGISTER EXPORT — repositions the vault as an insurance-ready
            asset schedule. CSV is generated on the server, signed by the user
            session, and includes every item with retailer / serial / replacement
            value / warranty status. */}
        <div className="border border-[#e4e4e7] bg-white p-5 mb-6 flex flex-wrap items-start justify-between gap-4" data-testid="vault-asset-register-strip">
          <div className="flex items-start gap-3 flex-1 min-w-[260px]">
            <ShieldCheck size={24} weight="duotone" color="#002FA7" className="flex-shrink-0 mt-1" />
            <div>
              <div className="font-heading font-bold tracking-tight">Your vault doubles as a home asset register.</div>
              <p className="text-sm text-[#52525b] mt-1 leading-relaxed max-w-2xl">
                Download a CSV of every item with retailer, purchase date, serial number, and replacement value, formatted for NZ insurers (Tower, AMI, State, AA). Fill in serial numbers and replacement values on each item page to make this even more useful at claim time.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await api.get("/vault/insurance-export.csv", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `priceguard-asset-register-${new Date().toISOString().slice(0,10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success("Asset register downloaded");
              } catch (e) {
                toast.error("Could not export. Try again in a moment.");
              }
            }}
            className="bg-[#0a0a0a] text-white font-bold px-5 py-3 inline-flex items-center gap-2 hover:bg-[#002FA7] transition-colors"
            data-testid="vault-export-asset-register-btn"
          >
            <DownloadSimple size={16} weight="bold" /> Export asset register (CSV)
          </button>
        </div>

        {plan && (
          <div className="bg-white border border-[#e4e4e7] px-5 py-3 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="vault-plan-meta">
            <span className="font-mono-pg text-xs uppercase tracking-widest text-[#52525b] inline-flex items-center gap-2">
              Plan · <span className="text-black">{plan.plan === "pro" ? "Pro" : "Free"}</span>
              {plan.launch_free && (
                <span className="text-[9px] font-bold tracking-widest bg-black text-white px-1.5 py-0.5 uppercase" data-testid="vault-plan-launch-free">Free during launch</span>
              )}
            </span>
            <span className="text-sm">
              <strong>{plan.used}</strong>{" "}
              {plan.plan === "pro"
                ? <span className="text-[#52525b]">items stored · unlimited storage included</span>
                : <>
                    {plan.cap != null && (<>of <strong>{plan.cap}</strong> items used (purchases + vault). </>)}
                    {plan.cap != null && plan.remaining <= 1 && (
                      <span className="text-[#D97706] font-bold ml-1">Approaching the limit.</span>
                    )}
                  </>
              }
            </span>
          </div>
        )}

        {/* Email-to-vault forwarding - feature wired but Resend Inbound not yet activated */}
        <div className="border border-[#e4e4e7] bg-[#f7f7f8] p-5 mb-8 flex flex-wrap gap-4 items-start opacity-75" data-testid="vault-forwarding-coming-soon">
          <EnvelopeSimple size={28} weight="duotone" color="#52525b" className="flex-shrink-0 mt-1" />
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="pg-overline text-[#52525b]">Auto-add receipts by email</div>
              <span className="text-[9px] font-bold tracking-widest bg-black text-white px-1.5 py-0.5 uppercase">Coming soon</span>
            </div>
            <p className="text-sm mt-1 leading-relaxed text-[#52525b]">
              Soon you'll be able to forward any order-confirmation email straight to your vault and we'll read it with AI, file it, and start the warranty clock automatically. You'll also be able to add multiple email addresses (work, personal, partner) so receipts can land from any inbox.
            </p>
            <p className="text-xs text-[#52525b] mt-3 font-mono-pg">
              For now, upload photos or PDFs of your receipts using the button below.
            </p>
          </div>
        </div>

        {loading ? <SkeletonGrid count={3} /> : items.length === 0 ? (
          <div className="bg-white border border-[#e4e4e7] p-12 text-center" data-testid="vault-empty">
            <VaultIcon size={48} weight="duotone" color="#002FA7" className="mx-auto" />
            <h2 className="font-heading text-2xl font-bold mt-4 tracking-tight">Your vault is empty</h2>
            <p className="text-[#52525b] mt-2 max-w-md mx-auto text-sm leading-relaxed">
              Add your first receipt or tracked purchase to start watching warranties and prices. We'll read each receipt with AI automatically and remind you 30 days before each warranty runs out.
            </p>
            <Link to="/dashboard/vault/new" className="mt-6 inline-flex items-center gap-2 bg-[#002FA7] text-white font-bold px-6 py-3 hover:bg-[#00207A]">
              <Plus size={18} weight="bold" /> Add a receipt
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((it) => {
              const isPurchase = it.source === "purchase";
              const detailHref = isPurchase ? `/dashboard/purchase/${it.purchase_id}` : `/dashboard/vault/${it.vault_id}`;
              return (
                <div key={it.id} className="bg-white border border-[#e4e4e7] p-5 hover:border-black transition-colors group" data-testid={`vault-item-${it.id}`}>
                  <Link to={detailHref} className="block">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="pg-overline truncate">{it.retailer || "Unknown retailer"}</div>
                        <h3 className="font-heading font-bold text-lg tracking-tight mt-1 truncate">
                          {it.product_name || it.category || "Receipt"}
                        </h3>
                        <div className="text-xs text-[#52525b] mt-1 font-mono-pg flex items-center gap-1.5 flex-wrap">
                          <CalendarBlank size={12} /> {formatDateNZ(it.purchase_date)}
                          {it.total != null && <span> · <strong className="text-black">${Number(it.total).toFixed(2)}</strong></span>}
                        </div>
                      </div>
                      {isPurchase
                        ? <ShieldCheck size={28} weight="duotone" color={it.status === "protected" ? "#002FA7" : "#52525b"} className="flex-shrink-0" />
                        : <Receipt size={28} weight="duotone" color="#002FA7" className="flex-shrink-0" />
                      }
                    </div>
                    <div className="border-t border-[#e4e4e7] mt-4 pt-3 flex items-center justify-between flex-wrap gap-2">
                      <UnifiedStatusPill it={it} />
                      <ArrowRight size={14} weight="bold" className="text-[#52525b] group-hover:text-black" />
                    </div>
                  </Link>
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-[10px] uppercase font-mono-pg tracking-widest text-[#52525b]" data-testid={`vault-receipt-state-${it.id}`}>
                      {it.has_receipt ? "Receipt on file" : "No receipt yet"}
                    </div>
                    {!isPurchase && (
                      <button onClick={() => del(it.vault_id)} className="text-[10px] uppercase font-mono-pg tracking-widest text-[#52525b] hover:text-[#FF3B30] flex items-center gap-1" data-testid={`vault-delete-${it.vault_id}`}>
                        <Trash size={11} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10">
          <FeedbackCTA
            variant="strip"
            prompt="Missing a retailer or warranty feature in the vault?"
            subject="Vault feature idea"
          />
        </div>
      </main>
    </div>
  );
}
